/**
 * 1.7.8 (D-1.7.8-3) — after a push: the pushed tenants with their new times, and "Email the tenant their new time"
 * per row, off by default; the office presses. Nothing is emailed without that press: each ticked row is one
 * `tellNewTimeReq`, which puts one email in the Outbox (the cancel window applies, as for every email). A tenant
 * with no email the app may use has no tick: "No email on file — tell them by phone".
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ToldRow } from '@shared/day-bend';
import { formatClock } from '@shared/dayplan';
import { ApiRequestError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/Icons';
import { useAnnounce } from '@/components/Announcer';
import { tellNewTimeReq } from './api';

export function WhoNeedsToKnow({ rows, onDone }: { rows: ToldRow[]; onDone: () => void }) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const heading = useRef<HTMLHeadingElement | null>(null);
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());
  /** Rows whose email is in the Outbox: not sent twice. */
  const [queued, setQueued] = useState<ReadonlySet<string>>(new Set());
  const [failed, setFailed] = useState<{ unit: string; why: string }[]>([]);
  const [count, setCount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    heading.current?.focus();
  }, []);

  const toSend = rows.filter((r) => r.canEmail && ticked.has(r.appointmentId) && !queued.has(r.appointmentId));

  const send = async () => {
    if (!toSend.length) return;
    setBusy(true);
    const ok: string[] = [];
    const bad: { unit: string; why: string }[] = [];
    for (const r of toSend) {
      try {
        await tellNewTimeReq(r.appointmentId);
        ok.push(r.appointmentId);
      } catch (e) {
        bad.push({ unit: r.unit, why: e instanceof ApiRequestError ? (e.body?.message ?? e.message) : (e as Error).message });
      }
    }
    setQueued((q) => new Set([...q, ...ok]));
    setFailed(bad);
    setCount(ok.length);
    setBusy(false);
    const said = [t('when178.told.queued', { count: ok.length }), ...bad.map((b) => t('when178.told.failed', b))].join(' ');
    announce(said, bad.length ? 'assertive' : 'polite');
  };

  return (
    <section className="told178" aria-labelledby="told-h" data-testid="told-list">
      <h3 id="told-h" tabIndex={-1} ref={heading}>
        {t('when178.told.title')}
      </h3>
      {rows.length ? (
        <>
          <ul className="told-rows">
            {rows.map((r) => {
              const id = r.appointmentId;
              const done = queued.has(id);
              return (
                <li key={id}>
                  <b id={`told-row-${id}`}>{t('when178.told.row', { unit: r.unit, name: r.name, from: formatClock(r.from), to: formatClock(r.to) })}</b>
                  {r.canEmail ? (
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={done || ticked.has(id)}
                        disabled={done || busy}
                        aria-describedby={`told-row-${id}`}
                        data-testid={`tell-${id}`}
                        onChange={(e) =>
                          setTicked((s) => {
                            const next = new Set(s);
                            if (e.target.checked) next.add(id);
                            else next.delete(id);
                            return next;
                          })
                        }
                      />
                      <span>{t('when178.told.tick')}</span>
                    </label>
                  ) : (
                    <p className="help" data-testid={`tell-none-${id}`}>
                      <Icon name="phone" aria-hidden="true" /> {t('when178.told.none')}
                    </p>
                  )}
                  {done ? (
                    <p className="help told-queued">
                      <Icon name="check" aria-hidden="true" /> {t('when178.told.queuedRow')}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ul>
          <p className="help">{t('when178.told.help')}</p>
          {count !== null ? (
            <p className="help told-result" data-testid="told-result">
              {t('when178.told.queued', { count })}
            </p>
          ) : null}
          {failed.length ? (
            <div className="warn take" role="alert" data-testid="told-failed">
              <Icon name="alert" aria-hidden="true" />
              <ul className="told-failed">
                {failed.map((f) => (
                  <li key={f.unit}>{t('when178.told.failed', f)}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      ) : (
        <p data-testid="told-nobody">{t('when178.told.nobody')}</p>
      )}
      <div className="told-actions">
        {rows.length ? (
          <Button variant="secondary" type="button" disabled={busy || !toSend.length} onClick={() => send()} data-testid="told-send">
            {busy ? t('when178.told.sending') : t('when178.told.send')}
          </Button>
        ) : null}
        <Button type="button" disabled={busy} onClick={onDone} data-testid="told-done">
          {t('when178.told.done')}
        </Button>
      </div>
    </section>
  );
}
